import React, { useState, useEffect } from 'react';
import { db } from '../firebase-config';
import { doc, onSnapshot, collection, query, orderBy } from 'firebase/firestore'; // <--- ENSURE THESE ARE PRESENT
import { en } from '../locales/en';
import { useUser } from '../UserContext';
import { openEvidenceFileWithAccessLog } from '../services/aiPipelines/schemaV2';
import { accreditationLevelLabel, resolveLiveAccreditationLevel } from '../utils/accreditationLevel';
import AccreditationLevelDeltaMark from '../components/AccreditationLevelDeltaMark';
import './dossier.css';

const SchoolDossier = ({ schoolId, onClose, onEditReport }) => {
  const { user } = useUser();
  const [data, setData] = useState({
    school: null,
    audit: null,
    report: null,
    forensic: null,
    distilled: null
  });
  const [lifecycle, setLifecycle] = useState([]); // <--- ADD THIS LINE
  const [activeStation, setActiveStation] = useState(null);
  const [viewForensic, setViewForensic] = useState(false);

  useEffect(() => {
    if (!schoolId) return;

    const refs = {
      school: doc(db, "schools", schoolId),
      audit: doc(db, "audits", schoolId),
      report: doc(db, "reports", schoolId),
      forensic: doc(db, "questionnaires_raw", schoolId),
      distilled: doc(db, "distilledData", schoolId)
    };

    const unsubscribes = Object.entries(refs).map(([key, ref]) => 
      onSnapshot(ref, (snap) => {
        setData(prev => ({ ...prev, [key]: snap.exists() ? snap.data() : null }));
      }, (err) => console.warn(`Dossier Sync Error [${key}]:`, err))
    );

    // --- ADD THIS SPECIFIC BLOCK FOR THE LIFECYCLE ---
    const lifecycleRef = collection(db, "schools", schoolId, "lifecycle");
    const q = query(lifecycleRef, orderBy("timestamp", "desc"));
    const unsubLifecycle = onSnapshot(q, (snap) => {
      setLifecycle(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });

    return () => {
    unsubscribes.forEach(unsub => unsub());
    unsubLifecycle(); // <--- CLEAN UP THE NEW LISTENER
  };
}, [schoolId]);

  if (!data.school) return <div className="dossier-loader">Syncing Master Archive...</div>;

  const { school, audit, report, forensic } = data;
  const isFinal = report?.meta?.isPublished || false;

  const getRegLabel = (qKey) => {
    try {
      const sections = en?.accreditationForm?.sections || [];
      for (const section of sections) {
        if (section.questions && section.questions[qKey]) return section.questions[qKey].label;
      }
    } catch (e) {}
    return qKey;
  };

  // NEW: Helper to render complex registration data
  const renderAnswerValue = (val) => {
    if (val === null || val === undefined) return "---";
    
    // Handle Training/Checklist Arrays
    if (Array.isArray(val)) {
      return (
        <div className="dossier-tag-list">
          {val.map((item, i) => <span key={i} className="dossier-tag">{item}</span>)}
        </div>
      );
    }

    // Handle Objects (Specialist Count / Need Profiles)
    if (typeof val === 'object') {
      return (
        <div className="dossier-nested-data">
          {Object.entries(val).map(([subKey, subVal]) => (
            <div key={subKey} className="nested-row">
              <span className="n-key">{subKey}:</span>
              <span className="n-val">{Array.isArray(subVal) ? subVal.join(', ') : String(subVal)}</span>
            </div>
          ))}
        </div>
      );
    }

    return <span className="primitive-val">{String(val)}</span>;
  };

  return (
    <div className="school-dossier-hud">
      <header className="dossier-header">
        <div className="dossier-header-left">
          <button onClick={onClose} className="btn-back-registry">← REGISTRY</button>
          <div className="dossier-identity">
            <h1>{school?.officialName || "Institution"}</h1>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <span className={`badge-stage ${resolveLiveAccreditationLevel(school)}`}>
                STAGE: {accreditationLevelLabel(school?.accreditationLevel).toUpperCase()}
                <AccreditationLevelDeltaMark school={school} />
              </span>
              <span className="accreditor-assignment">
                👤 ASSIGNED: {school?.assignedAuditorName || report?.certification?.accreditorName || "TBD (No Auditor Assigned)"}
              </span>
            </div>
          </div>
        </div>
        <div className="dossier-header-right">
          <button className="btn-open-editor" onClick={() => onEditReport(schoolId)}>
            {isFinal ? "VIEW/AMEND FINAL REPORT" : "WRITE / VIEW REPORT"}
          </button>
        </div>
      </header>

      <div className="dossier-grid">
        <aside className="dossier-col dna-col">
          <section className="dossier-card full-dna-archive">
            <h3>Institutional Profile</h3>
            <div className="archive-list">
  {(en?.accreditationForm?.sections || []).map((section, sectionIdx) => {
    // 1. Get the questions for this section
    const entries = Object.entries(section.questions);

    // 2. APPLY REGISTER.JS ORDERING LOGIC (The Identity Filter)
    const orderedEntries = sectionIdx === 0 ? [
      ...entries.filter(([k]) => k === 'officialName'),
      ...entries.filter(([k]) => k === 'officialAddress'),
      ...entries.filter(([k]) => k === 'officialAddressCountry'),
      ...entries.filter(([k]) => !['officialName', 'officialAddress', 'officialAddressCountry'].includes(k))
    ] : entries;

    return (
      <React.Fragment key={section.id}>
        {orderedEntries.map(([key, qMetadata]) => {
          const val = school?.answers?.[key];
          
          // Only show if the institutional answer exists
          if (val === undefined || val === null || val === "") return null;

          return (
            <div key={key} className="archive-item">
              <label className="reg-question-label">{qMetadata.label}</label>
              <div className="reg-answer-value">{renderAnswerValue(val)}</div>
            </div>
          );
        })}
      </React.Fragment>
    );
  })}
</div>
            <div className="forensic-trigger-box">
               <button className={`btn-forensic ${viewForensic ? 'active' : ''}`} onClick={() => setViewForensic(!viewForensic)}>
                 {viewForensic ? "CLOSE FORENSIC INDEX" : "VIEW GLOBAL INCLUSION INDEX (LENS B)"}
               </button>
            </div>
          </section>
        </aside>

        <main className="dossier-col core-col">
          {viewForensic ? (
            <section className="dossier-card forensic-view">
              <h3>Global Inclusion Index: Raw Forensic Responses</h3>
              <div className="forensic-data-grid">
                {forensic?.responses ? (
                  Object.entries(forensic.responses).map(([qId, val]) => (
                    <div key={qId} className="f-data-row">
                      <label>{qId}</label>
                      <span>{typeof val === 'object' ? JSON.stringify(val) : String(val)}</span>
                    </div>
                  ))
                ) : <p className="empty-msg">No forensic index data submitted.</p>}
              </div>
            </section>
          ) : (
            <>
              <div className="source-indicator-bar">
                <span className={`source-badge ${isFinal ? 'final' : 'draft'}`}>
                  {isFinal ? '✓ DISPLAYING FINALIZED ACCREDITATION REPORT' : '⚠ DISPLAYING RAW INSTITUTIONAL SEF (SUBMISSION)'}
                </span>
              </div>
              <div className="station-archive-list">
                {(en?.stations || []).map((s) => {
                  const isActive = activeStation === s.id;
                  const selfRating = audit?.stations?.[s.id]?.selectedLevel;
                  const finalRating = report?.stationAnalysis?.[s.id]?.ifipRating;
                  return (
                    <div key={s.id} className={`station-archive-card ${isActive ? 'active' : ''}`}>
                      <div className="archive-card-header" onClick={() => setActiveStation(isActive ? null : s.id)}>
                        <div className="s-id">{s.id}</div>
                        <div className="s-info">
                          <h4>{s.title}</h4>
                          <div className="rating-comparison">
                             <span>SCHOOL SEF: <strong>L{selfRating || '-'}</strong></span>
                             {isFinal && <span>IFIP RATING: <strong style={{color: '#d11d7e'}}>L{finalRating || '-'}</strong></span>}
                          </div>
                        </div>
                        <div style={{fontSize: '10px'}}>{isActive ? "▲" : "▼"}</div>
                      </div>
                      {isActive && (
                        <div className="archive-card-body">
                          <p className="narrative-text">
                            {isFinal ? report?.stationAnalysis?.[s.id]?.content : audit?.stations?.[s.id]?.narrative || "No narrative provided."}
                          </p>
                          <div className="station-meta-footer">
                            <span className="timestamp">DATA ACCESSED: {new Date().toLocaleDateString()}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </main>

        <aside className="dossier-col vault-col">
          <section className="dossier-card vault-card">
            <h3>Evidence Repository</h3>
            <div className="file-scroll-list">
              {(audit?.evidenceVault || []).map((file, i) => {
                const distilledFile = data.distilled?.factSheet?.find(f => f.fileName === file.name);
                return (
                  <div
                    key={i}
                    className="file-item-mini"
                    onClick={() =>
                      openEvidenceFileWithAccessLog({
                        schoolId,
                        file,
                        actor: user,
                        surface: 'school_dossier',
                      })
                    }
                  >                    <div style={{fontSize: '14px'}}>📄</div>
                    <div className="f-meta">
                      <span className="f-name">{file.name}</span>
                      <span className="f-tags">{file.tags?.join(', ') || 'untagged'}</span>
                      {distilledFile && (
                        <span style={{ 
                          fontSize: '0.65rem', 
                          opacity: 0.7, 
                          fontStyle: 'italic', 
                          marginTop: '4px', 
                          color: 'var(--text-dim)',
                          lineHeight: '1.2' 
                        }}>
                          {distilledFile.evidenceSummary}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
              {(!audit?.evidenceVault || audit.evidenceVault.length === 0) && <p className="empty-msg">No files uploaded.</p>}
            </div>
          </section>
          {/* --- UPDATED SIDEBAR IN SchoolDossier.js --- */}
<section className="dossier-card timeline-card">
  <h3>Institutional Lifecycle</h3>
  <div className="timeline-thread">
    {lifecycle.length > 0 ? lifecycle.map((event, idx) => (
      <div key={idx} className="t-item" style={{ 
        borderLeft: '2px solid rgba(19, 138, 138, 0.3)', 
        paddingLeft: '15px', 
        marginBottom: '20px', 
        position: 'relative' 
      }}>
        <div style={{ 
          position: 'absolute', left: '-6px', top: '0', 
          width: '10px', height: '10px', borderRadius: '50%', 
          background: event.type.includes('RELEASED') ? '#d11d7e' : '#138a8a' 
        }}></div>
        <p style={{ margin: 0, fontWeight: 'bold', fontSize: '0.85rem', color: 'white' }}>
          {event.eventLabel || event.type.replace(/_/g, ' ')}
        </p>
        <div style={{ fontSize: '0.7rem', opacity: 0.6, marginTop: '4px' }}>
          {event.adminName} • {event.timestamp?.toDate().toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
        </div>
        {event.recipient && (
          <div style={{ fontSize: '0.65rem', color: '#138a8a', marginTop: '2px' }}>
            ✉ {event.recipient}
          </div>
        )}
      </div>
    )) : (
      <p style={{ fontSize: '0.8rem', opacity: 0.4, fontStyle: 'italic' }}>No lifecycle events recorded.</p>
    )}
  </div>
</section>
        </aside>
      </div>

      <footer className="dossier-footer">
         <details>
            <summary className="btn-danger-link">ADMINISTRATIVE COMPLIANCE & PURGE</summary>
            <div className="danger-box">
              <p>
                Selective school purge is available from the registry row under <strong>Portal Security</strong>
                (requires the <em>Purge School Data</em> permission).
              </p>
            </div>
          </details>
      </footer>
    </div>
  );
};

export default SchoolDossier;